<template>
  <div class="home">
    <header class="hero">
      <h1 class="hero-title">数据贯通成熟度评价</h1>

      <!-- 评价深度：快速版 17 项 / 完整版 41 项 -->
      <div class="segmented" role="tablist" aria-label="评价深度">
        <button
          v-for="ed in editions"
          :key="ed.id"
          type="button"
          role="tab"
          :aria-selected="ed.id === edition"
          class="seg-item"
          :class="{ 'is-active': ed.id === edition }"
          @click="pickEdition(ed.id)"
        >
          <span class="seg-name">{{ ed.name }}</span>
          <span class="seg-meta">{{ ed.count }} 项 · {{ ed.desc }}</span>
        </button>
      </div>
    </header>

    <section class="choices">
      <article
        v-for="opt in scopes"
        :key="opt.id"
        class="choice"
        :class="{ 'is-active': opt.id === scope }"
        role="button"
        tabindex="0"
        @click="enter(opt.id)"
        @keydown.enter.prevent="enter(opt.id)"
        @keydown.space.prevent="enter(opt.id)"
      >
        <div class="choice-badge">{{ opt.badge }}</div>
        <h2 class="choice-title">{{ opt.name }}</h2>
        <p class="choice-tagline">{{ opt.tagline }}</p>

        <ul class="choice-facts">
          <li v-for="f in opt.facts" :key="f.k">
            <span class="fact-k">{{ f.k }}</span>
            <span class="fact-v">{{ f.v }}</span>
          </li>
        </ul>

        <div class="choice-cta">
          <span>开始评价</span>
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
            <path d="M5 12h13M13 6l6 6-6 6" fill="none" stroke="currentColor"
                  stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>
      </article>
    </section>

    <footer class="home-foot">
      <button v-if="hasProgress" type="button" class="resume" @click="$emit('continue')">
        继续上次评价<template v-if="resumeLabel">（{{ resumeLabel }}）</template>
      </button>
    </footer>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { EDITIONS, SCOPES, ONLINE_INDICATORS, FULL_INDICATORS } from '../data/index.js'

const props = defineProps({
  scope: { type: String, default: '' },
  edition: { type: String, default: 'online' },
  hasProgress: { type: Boolean, default: false },
  resumeLabel: { type: String, default: '' }
})

const emit = defineEmits(['update:edition', 'select', 'continue'])

const editions = computed(() => [
  {
    id: 'online',
    name: EDITIONS.online.name,
    desc: '一次约 15 分钟',
    count: ONLINE_INDICATORS.enterprise.length
  },
  {
    id: 'full',
    name: EDITIONS.full.name,
    desc: '全面诊断',
    count: FULL_INDICATORS.enterprise.length
  }
])

// 卡片要点取自方案文档表 1《企业级与项目级数据贯通评价的核心区别》
const scopes = [
  {
    id: 'project',
    badge: '单项目',
    name: SCOPES.project.name,
    tagline: '抓执行 · 抓贯通 · 抓实效',
    facts: [
      { k: '评价核心', v: '验证项目对企业数据标准、编码规则、BIM 交付标准的实际执行力度' },
      { k: '数据范围', v: '单一项目周期内的进度、成本、质量、安全、BIM 模型等实时/阶段数据' },
      { k: '角色定位', v: '项目经理、项目总工、BIM 专员等执行/操作/管理层' },
      { k: '佐证特征', v: '过程/记录/报告类，须来源于项目现场' }
    ]
  },
  {
    id: 'enterprise',
    badge: '集团/分公司',
    name: SCOPES.enterprise.name,
    tagline: '建标准 · 建平台 · 建能力',
    facts: [
      { k: '评价核心', v: '评估是否具备统一的数据标准体系、数据中台能力与跨项目复用机制' },
      { k: '数据范围', v: '全企业历史项目积累、企业级主数据标准与企业级数据资产目录' },
      { k: '角色定位', v: '企业高管、信息中心、标准处、数科公司等决策/管理/研发层' },
      { k: '佐证特征', v: '制度/架构/清单类，如标准规范、中台架构图、数据字典' }
    ]
  }
]

function pickEdition (id) {
  if (id === props.edition) return
  emit('update:edition', id)
}

function enter (id) {
  emit('select', { scope: id, edition: props.edition })
}
</script>

<style lang="scss" scoped>
.home {
  max-width: 1180px;
  margin: 0 auto;
  padding: clamp(40px, 7vw, 88px) 24px 24px;
  text-align: center;
}

.hero-title {
  margin: 0;
  font-size: clamp(30px, 5vw, 56px);
  font-weight: 600;
  letter-spacing: 0.04em;
  color: #1d1d1f;
  background: linear-gradient(180deg, #1d1d1f 0%, #4a4a52 100%);
  -webkit-background-clip: text;
  background-clip: text;
}

/* 版本切换：苹果风格分段控件 */
.segmented {
  display: inline-flex;
  gap: 4px;
  margin-top: 34px;
  padding: 4px;
  border-radius: 999px;
  background: rgba(120, 120, 128, 0.12);
}

.seg-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 168px;
  padding: 10px 24px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #1d1d1f;
  cursor: pointer;
  transition: background 0.28s ease, box-shadow 0.28s ease, transform 0.28s ease;

  &.is-active {
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.10), 0 6px 18px rgba(0, 0, 0, 0.06);
  }

  &:hover:not(.is-active) {
    background: rgba(255, 255, 255, 0.55);
  }
}

.seg-name {
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.04em;
}

.seg-meta {
  font-size: 12px;
  color: #86868b;
}

.choices {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  margin-top: 48px;
  text-align: left;
}

.choice {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 34px 32px 28px;
  border-radius: 24px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  overflow: hidden;
  transition: transform 0.32s cubic-bezier(0.22, 1, 0.36, 1),
              box-shadow 0.32s ease, border-color 0.32s ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(160deg, rgba(0, 113, 227, 0.10), rgba(123, 220, 255, 0.04) 55%, transparent);
    opacity: 0;
    transition: opacity 0.32s ease;
  }

  &:hover,
  &:focus-visible {
    transform: translateY(-6px);
    border-color: rgba(0, 113, 227, 0.35);
    box-shadow: 0 18px 44px rgba(0, 113, 227, 0.16);
    outline: none;

    &::before {
      opacity: 1;
    }

    .choice-cta {
      color: #0071e3;

      svg {
        transform: translateX(4px);
      }
    }
  }

  > * {
    position: relative;
  }
}

.choice-badge {
  align-self: flex-start;
  padding: 5px 12px;
  font-size: 12px;
  letter-spacing: 0.1em;
  color: #0071e3;
  background: rgba(0, 113, 227, 0.10);
  border-radius: 999px;
}

.choice-title {
  margin: 18px 0 0;
  font-size: clamp(24px, 3vw, 34px);
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #1d1d1f;
}

.choice-tagline {
  margin: 10px 0 0;
  font-size: 15px;
  letter-spacing: 0.16em;
  color: #0071e3;
}

.choice-facts {
  margin: 22px 0 0;
  padding: 0;
  list-style: none;
  flex: 1;

  li {
    display: flex;
    gap: 12px;
    padding: 11px 0;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    font-size: 13.5px;
    line-height: 1.7;

    &:first-child {
      border-top: 0;
    }
  }
}

.fact-k {
  flex: 0 0 68px;
  color: #86868b;
}

.fact-v {
  flex: 1;
  color: #3a3a3c;
}

.choice-cta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 26px;
  font-size: 15px;
  font-weight: 500;
  color: #06c;
  transition: color 0.28s ease;

  svg {
    transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1);
  }
}

.home-foot {
  margin-top: 40px;
}

.resume {
  padding: 11px 30px;
  font-size: 14px;
  color: #ffffff;
  background: #0071e3;
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.25s ease, transform 0.25s ease;

  &:hover {
    background: #0077ed;
    transform: translateY(-1px);
  }
}

@media (max-width: 860px) {
  .choices {
    grid-template-columns: minmax(0, 1fr);
  }

  .segmented {
    display: flex;
    width: 100%;
    max-width: 420px;
  }

  .seg-item {
    flex: 1;
    min-width: 0;
    padding: 10px 12px;
  }
}
</style>
